/** @jsxImportSource solid-js */

import * as CDM from 'trimble-connect-sdk';
import {
  Accessor,
  Component,
  createContext,
  createSignal,
  JSX,
  useContext,
} from 'solid-js';
import { AttachmentPickerApiService } from './AttachmentPickerApiService';
import type {
  FolderInfo,
  FolderContentsResponse,
  TCPSServiceData,
} from './types';

interface TCPSService {
  readonly data: Accessor<TCPSServiceData>;
  fetchRootFolder(project: CDM.Project): Promise<FolderInfo>;
  fetchFolderContents(
    project: CDM.Project,
    folderId: string,
    options?: {
      readonly offset?: number;
      readonly limit?: number;
      readonly sort?: string;
    }
  ): Promise<FolderContentsResponse>;
  fetchFolder(project: CDM.Project, folderId: string): Promise<FolderInfo>;
  setCurrentFolder(folder: FolderInfo | null): void;
  setPreviousFolder(folder: FolderInfo | null): void;
  setLoading(loading: boolean): void;
  setError(error: string | null): void;
}

const TCPSServiceContext = createContext<TCPSService>();

export interface TCPSServiceProviderProps {
  readonly children?: JSX.Element;
}

export const TCPSServiceProvider: Component<TCPSServiceProviderProps> = (
  props: TCPSServiceProviderProps
) => {
  const [isLoading, setIsLoading] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [currentFolder, setCurrentFolder] = createSignal<FolderInfo | null>(
    null
  );
  const [previousFolder, setPreviousFolder] = createSignal<FolderInfo | null>(
    null
  );

  const fetchRootFolder = async (project: CDM.Project): Promise<FolderInfo> => {
    try {
      setIsLoading(true);
      setError(null);

      const folderInfo = await AttachmentPickerApiService.getFolder(
        project,
        project.rootId
      );

      return folderInfo;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch root folder';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFolder = async (
    project: CDM.Project,
    folderId: string
  ): Promise<FolderInfo> => {
    try {
      setIsLoading(true);
      setError(null);

      const folderInfo = await AttachmentPickerApiService.getFolder(
        project,
        folderId
      );

      return folderInfo;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch folder';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFolderContents = async (
    project: CDM.Project,
    folderId: string,
    options: {
      readonly offset?: number;
      readonly limit?: number;
      readonly sort?: string;
    } = {}
  ): Promise<FolderContentsResponse> => {
    try {
      setIsLoading(true);
      setError(null);

      const defaultLimit = 25;
      const { offset = 0, limit = defaultLimit, sort } = options;

      // Use the AttachmentPickerApiService for consistent API calls
      const config = {
        headers: AttachmentPickerApiService.createPaginationHeaders(
          offset,
          limit
        ),
      };

      const response = await AttachmentPickerApiService.listFolderEntries(
        project.origin,
        folderId,
        config,
        sort
      );

      const contentRange = response.headers['content-range'] || '0/0';
      const { total: totalCount } =
        AttachmentPickerApiService.parseContentRange(contentRange);

      return {
        items: response.data,
        totalCount,
        hasMore: offset + limit < totalCount,
      };
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch folder contents';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const service: TCPSService = {
    data: () => ({
      isLoading: isLoading(),
      error: error(),
      currentFolder: currentFolder(),
      previousFolder: previousFolder(),
    }),
    fetchRootFolder,
    fetchFolderContents,
    fetchFolder,
    setCurrentFolder,
    setPreviousFolder,
    setLoading: setIsLoading,
    setError,
  };

  return (
    <TCPSServiceContext.Provider value={service}>
      {props.children}
    </TCPSServiceContext.Provider>
  );
};

export const useTCPSService = (): TCPSService => {
  const context = useContext(TCPSServiceContext);
  if (context === undefined) {
    throw new Error('useTCPSService must be used within a TCPSServiceProvider');
  }

  return context;
};
